import { format } from 'date-fns'
import i18next from 'i18next'
import React, { useState, useEffect } from 'react'
import { Alert, Modal, Button } from 'react-bootstrap'

const { t } = i18next

// Funzione per convertire l'array EXIF in formato decimale
const convertToDecimal = gpsArray => {
  if (!gpsArray || gpsArray.length < 3) return null
  const [degrees, minutes, seconds] = gpsArray
  return degrees + minutes / 60 + seconds / 3600
}

// Funzione per calcolare la distanza tra due coordinate GPS (in km)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null
  const R = 6371 // Raggio della Terra in km
  const dLat = (lat2 - lat1) * (Math.PI / 180)
  const dLon = (lon2 - lon1) * (Math.PI / 180)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c // distanza in km
  return distance
}

// Funzione per determinare se una foto è scattata da un cellulare
const evaluateExif = (exif, locationLat, locationLon, date) => {
  let isMobilePhoto = false
  const deviceMake = exif.Make?.toLowerCase()
  const deviceModel = exif.Model?.toLowerCase()

  // Verifica se Make e Model sono avvalorati (dispositivo mobile)
  if ((deviceMake && deviceModel) || exif.GPSLatitude || exif.GPSLongitude || exif.GPSAltitude) {
    isMobilePhoto = true
  }

  // Convertire le coordinate GPS in formato decimale
  const photoLat = convertToDecimal(exif.GPSLatitude)
  const photoLon = convertToDecimal(exif.GPSLongitude)

  // Calcolare la distanza tra la foto e il luogo
  const distance = calculateDistance(locationLat, locationLon, photoLat, photoLon)

  // Verifica se la foto è vicina al luogo (ad esempio, 100 metri di distanza)
  const isNearLocation = distance ? distance <= 0.1 : false // 100 metri

  // Confronta la data della foto con la data della visita
  let isVisitDateValid = false
  let photoDate = null
  let visitDate = null

  if (exif.DateTime && date) {
    photoDate = new Date(exif.DateTime)
    visitDate = new Date(date) // data della visita

    const fourHoursBefore = new Date(visitDate)
    fourHoursBefore.setHours(visitDate.getHours() - 4) // 4 ore prima della visita

    const fourHoursAfter = new Date(visitDate)
    fourHoursAfter.setHours(visitDate.getHours() + 4) // 4 ore dopo la visita

    // Verifica se la data della foto è dentro la finestra temporale
    isVisitDateValid = photoDate >= fourHoursBefore && photoDate <= fourHoursAfter
  }

  return { isMobilePhoto, isNearLocation, isVisitDateValid, photoDate, visitDate, distance }
}

const PhotoEvaluation = ({ exif, locationLat, locationLon, date }) => {
  const [alertVariant, setAlertVariant] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [photoDate, setPhotoDate] = useState(null)
  const [visitDate, setVisitDate] = useState(null)
  const [isVisitDateValid, setIsVisitDateValid] = useState(false)
  const [distance, setDistance] = useState(null)

  useEffect(() => {
    const {
      isMobilePhoto,
      isNearLocation,
      isVisitDateValid,
      photoDate: exifPhotoDate,
      visitDate: exifVisitDate,
      distance: exifDistance,
    } = evaluateExif(exif, locationLat, locationLon, date)

    setPhotoDate(exifPhotoDate)
    setVisitDate(exifVisitDate)
    setIsVisitDateValid(isVisitDateValid)
    setDistance(exifDistance)

    if (isMobilePhoto && isNearLocation && isVisitDateValid) {
      setAlertVariant('success') // Foto valida (cellulare, vicino al luogo, data corretta)
    } else if (isMobilePhoto && (isNearLocation || isVisitDateValid)) {
      setAlertVariant('warning') // Foto dubbiosa (vicino al luogo, ma non da cellulare o data sbagliata)
    } else {
      setAlertVariant('danger') // Foto non valida
    }
  }, [exif, locationLat, locationLon, date]) // La valutazione avviene quando uno di questi valori cambia

  const handleModalClose = () => setShowModal(false)
  const handleModalShow = () => setShowModal(true)

  return (
    <div>
      <Alert
        variant={alertVariant}
        className="text-center"
        onClick={handleModalShow}
        style={{ cursor: 'pointer', width: 'fit-content', fontSize: '1rem', padding: '0.5rem' }}>
        {alertVariant === 'success' && <i className="fas fa-check-circle text-success me-0"></i>}
        {alertVariant === 'warning' && <i className="fas fa-exclamation-triangle text-warning me-0"></i>}
        {alertVariant === 'danger' && <i className="fas fa-times-circle text-danger me-0"></i>}
      </Alert>

      <Modal backdrop="static" centered show={showModal} onHide={handleModalClose}>
        <Modal.Header>
          <Modal.Title>{t('components.photo_evaluation.modal_title', 'Valutazione Foto')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert>
            <ul>
              <li>
                <strong>{t('components.photo_evaluation.device_brand', 'Marca del dispositivo')}:</strong>{' '}
                {exif.Make || 'N/A'}
              </li>
              <li>
                <strong>{t('components.photo_evaluation.device_model', 'Modello del dispositivo')}:</strong>{' '}
                {exif.Model || 'N/A'}
              </li>
              <li>
                <strong>{t('components.photo_evaluation.gps_latitude', 'Latitudine GPS')}:</strong>{' '}
                {exif.GPSLatitude ? exif.GPSLatitude.join('° ') : 'N/A'}
              </li>
              <li>
                <strong>{t('components.photo_evaluation.gps_longitude', 'Longitudine GPS')}:</strong>{' '}
                {exif.GPSLongitude ? exif.GPSLongitude.join('° ') : 'N/A'}
              </li>
              <li>
                <strong>{t('components.photo_evaluation.gps_altitude', 'Altitudine GPS')}:</strong>{' '}
                {exif.GPSAltitude ? exif.GPSAltitude : 'N/A'}
              </li>
              <li>
                <strong>{t('components.photo_evaluation.resolution', 'Risoluzione')}:</strong> {exif.ImageWidth}x
                {exif.ImageHeight || 'N/A'}
              </li>
              <li>
                <strong>{t('components.photo_evaluation.iso', 'ISO')}:</strong> {exif.ISOSpeedRatings || 'N/A'}
              </li>
              <li>
                <strong>{t('components.photo_evaluation.flash', 'Flash')}:</strong>
                {exif.Flash || 'N/A'}
              </li>
            </ul>
          </Alert>

          {/* Confronto delle date e distanza */}
          <Alert variant={alertVariant}>
            <b>{t('components.photo_evaluation.photo_date', 'Data della foto')}:</b>{' '}
            {photoDate ? format(photoDate, 'yyyy-MM-dd HH:mm:ss') : 'N/A'}
            <br />
            <b>{t('components.photo_evaluation.visit_date', 'Data della visita')}:</b>{' '}
            {visitDate ? format(visitDate, 'yyyy-MM-dd HH:mm:ss') : 'N/A'}
            <br />
            <b>{t('components.photo_evaluation.date_comparison', 'Confronto delle date')}:</b>{' '}
            {isVisitDateValid
              ? t('components.photo_evaluation.valid_date', 'Data valida')
              : t('components.photo_evaluation.invalid_date', 'Data non valida')}
            <br />
            <b>{t('components.photo_evaluation.distance_from_location', 'Distanza dal luogo')}:</b>{' '}
            {distance ? `${distance.toFixed(2)} km` : 'N/A'}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            {t('components.photo_evaluation.close', 'Chiudi')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default PhotoEvaluation
